import { Immutable } from "immer";
import React from "react";
import { StarRating } from "tsi-common-react/src/components/StarRating/index";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { LandingCustomerReviewsBlock as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from the
// `customer_reviews_block` gate in `cms/landing_page.html` verbatim — every
// classname is load-bearing for the existing SCSS
// (`_customer-reviews-block.scss`), so keep them in sync.
type IProps = Immutable<{
    value: Value;
}>;

// Mirror the legacy `{% round_to_half product.rating|to_float %}` filter, which
// is `round(value * 2) / 2` in Python. Python's `round` uses banker's rounding
// (round-half-to-even), so an exact `x.25` input (e.g. mean of [3,3,3,4] = 3.25)
// rounds the `2x + 0.5` tie DOWN to even `x.0`, not up. JS `Math.round` is
// round-half-up and would render `x.5` instead — diverging from legacy. Replicate
// the round-half-to-even tie-break so the rendered star count matches exactly.
const roundToHalf = (value: number): number => {
    const doubled = value * 2;
    const floor = Math.floor(doubled);
    const diff = doubled - floor;
    if (diff < 0.5) {
        return floor / 2;
    }
    if (diff > 0.5) {
        return (floor + 1) / 2;
    }
    return (floor % 2 === 0 ? floor : floor + 1) / 2;
};

export const LandingCustomerReviewsBlock = (
    props: IProps,
): React.JSX.Element => {
    const v = props.value;
    // watchCSS gates the carousel to the mobile breakpoint (see
    // `_customer-reviews-block.scss`'s `content: "flickity"` marker);
    // initialIndex starts on the second review, matching the legacy behavior.
    const reviewListRef = React.useRef<HTMLDivElement>(null);
    useFlickity(reviewListRef, {
        options: { watchCSS: true, initialIndex: 1 },
    });
    // `product_review_info` is nullable in the serialized type; guard each
    // field so the block still renders when product data is missing.
    const info = v.product_review_info;
    const rating = info?.rating ?? null;
    const numReviews = info?.num_reviews ?? 0;
    const pdpUrl = info?.pdp_url ?? "";
    const reviews = v.reviews ?? [];

    // Apply the legacy `{% round_to_half product.rating|to_float %}` before
    // StarRating. StarRating's thresholds (>= i+0.25 half, >= i+0.75 full) match
    // legacy `star-rating.html` for most values, but diverge at exact `x.25`
    // inputs: round_to_half's banker's rounding sends `x.25` to `x.0` (outline),
    // whereas the raw value crosses the half threshold (half star). Legacy had no
    // `|default:0`, but a null rating renders all-outline either way, so `?? 0`
    // before rounding preserves that.
    const starRating = roundToHalf(rating ?? 0);

    return (
        <div className="l-full-width customer-reviews-block customer-reviews-block--landing-page landing-page__section landing-page__section--padding">
            <div className="l-capped-width">
                <div className="customer-reviews-block__copy">
                    <h2 className="customer-reviews-block__title landing-page__title">
                        {v.reviews_title}
                    </h2>
                    {/* Legacy wrapped star-rating.html (which itself emits a
                        `<div class="star-rating ...">`) inside an outer
                        `<div class="star-rating">`, producing a deliberate
                        double `.star-rating` nesting. StarRating renders the
                        inner wrapper; reproduce the outer wrapper here. The
                        legacy `class=ratingClasses` arg was undefined in this
                        context, so no className is passed to StarRating. */}
                    <div className="star-rating">
                        <StarRating rating={starRating} />
                    </div>
                    <div className="customer-reviews-block__rating-copy">
                        {numReviews}+ REVIEWS
                        <br />
                        {/* `rating|floatformat:1` → `toFixed(1)`; Django's
                            floatformat of None renders empty. */}
                        AVERAGE OF {rating !== null ? rating.toFixed(1) : ""}{" "}
                        STARS
                    </div>
                </div>
                {/* `landign-page-customer-review` is a typo carried over from
                    the legacy template — preserved verbatim. */}
                <div
                    className="customer-reviews-block__review-list landign-page-customer-review"
                    ref={reviewListRef}
                >
                    {reviews.map((review, i) => (
                        <div key={i} className="customer-reviews-block__review">
                            <div className="customer-reviews-block__review-headline">
                                {review.title}
                            </div>
                            <div className="customer-reviews-block__review-copy">
                                {review.description}
                            </div>
                            <div className="customer-reviews-block__review-owner">
                                {review.author}
                            </div>
                        </div>
                    ))}
                </div>
                {/* `al-customer-reviews-block__button` is an analytics hook —
                    it MUST survive verbatim on the CTA. */}
                <a
                    className="button button--ming customer-reviews-block__button al-customer-reviews-block__button"
                    href={`${pdpUrl}#reviews`}
                >
                    <span>{v.review_link_name}</span>
                </a>
            </div>
        </div>
    );
};
